// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { faChevronDown, faChevronUp, faFlask, faGlobe, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Types
import { TPartnerResultDTO } from "Types";
// styles
import styles from "./partnerResult.module.scss";
// Controllers
import { PartnerControllerSingleton, WikipediaControllerSingleton } from "Controllers";
// Components
import { FindestBookmark, SidebarFilterMenu, TitledTypedChild } from "Components";
import { DocumentCountBox } from "../DocumentCountBox";
import { DecoupledPatentDocuments } from "../DecoupledDocuments/DecoupledPatentDocuments/DecoupledPatentDocuments";
import { DecoupledScienceDocuments, DecoupledWebDocuments } from "../DecoupledDocuments";
import { PartnerTypeBox } from "./PartnerTypeBox";
// Enums
import { DocumentTypeEnum, EntityTypeEnum } from "Enums";
// Logic
import { BatchFunctionLogicSingleton } from "Logics";
// Contexts
import { CaseContext, AuthContext } from "Stores";

type TPartnerResultProps = {
    partner: TPartnerResultDTO,
    handleOrganizationHasBeenUnbookmarked?: (unbookmarkOnEntityId: string) => Promise<void>,
    handleUnderneathDocumentHasBeenUnbookmarked?: (unbookmarkOnEntityId: string) => Promise<void>,
    handleUnderneathDocumentHasBeenBookmarked?: (bookmarkOnEntityId: string) => Promise<void>,
    onHighlight?: (documentId: string, documentType: DocumentTypeEnum) => void,
}

export const PartnerResult: FC<TPartnerResultProps> = ({partner, handleOrganizationHasBeenUnbookmarked, handleUnderneathDocumentHasBeenUnbookmarked, handleUnderneathDocumentHasBeenBookmarked, onHighlight}: TPartnerResultProps) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [description, setDescription] = useState<string>("");
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
    const [openedDocumentType, setOpenedDocumentType] = useState<string | undefined>(undefined);

    // Contexts
    const { caseState } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);

    const getPartnerUrl = useCallback(async (): Promise<void> => {
        const imageUrl = await PartnerControllerSingleton.getImageAsync(partner.name);
        if (imageUrl) setImageUrl(imageUrl);
    }, [partner, setImageUrl]);

    const getWikiDescription = useCallback(async (): Promise<void> => {

        BatchFunctionLogicSingleton.addFunction("Wikipedia", async () => {
            const wikiDescription = await WikipediaControllerSingleton.getDescriptionAsync(partner.name);
            if(wikiDescription) { 
                setDescription(wikiDescription);
            }
        });

        BatchFunctionLogicSingleton.startIfNeeded("Wikipedia");

    }, [partner, setDescription]);

    useEffect(() => {
        setImageUrl(undefined);
        getPartnerUrl();
        getWikiDescription();
    }, [partner, getPartnerUrl, getWikiDescription]);

    const getTextClassName = (): string => {
        const classNameList = [styles.partnerDescription];
        if (isCollapsed) classNameList.push(styles.isCollapsed);
        return classNameList.join(" ");
    };

    const getReadMoreClassName = (): string => {
        const classNameList = [styles.readMoreButton];
        if (imageUrl) classNameList.push(styles.isImageVisible);
        return classNameList.join(" ");
    };

    const toggleIsCollapsed = (): void => {
        setIsCollapsed(!isCollapsed);
    };

    const onDocumentBoxClick = (type: string): void => {
        if (type === openedDocumentType) {
            setOpenedDocumentType(undefined);
            return;
        }

        setOpenedDocumentType(type);
    };

    const onScienceHighlight = (documentId: number) => {
        if(!onHighlight) return;
        onHighlight(documentId.toString(), DocumentTypeEnum.ScienceArticle);
    };

    const onPatentHighlight = (documentId: string) => {
        if(!onHighlight) return;
        onHighlight(documentId, DocumentTypeEnum.Patent);
    };
    
    return (
        <div className={styles.partnerResult}>
            <div className={styles.partnerHeader}>
                <FindestBookmark 
                    entity={partner}
                    entityType={EntityTypeEnum.OrganizationType}
                    isEditable={authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery}
                    isEntityBookmarked={partner.isBookmarked}
                    coworkerBookmarkOn={partner.coworkerBookmarkOn}
                    handleEntityHasBeenUnbookmarked={handleOrganizationHasBeenUnbookmarked} />
                <span className={styles.partnerTitle}>{partner.name}</span>
                <PartnerTypeBox partnerType={partner.partnerType} />
            </div>
            <div className={styles.partnerContent}>
                <div className={styles.partnerDescriptionContainer}>
                    {imageUrl ?
                        <img src={imageUrl} alt={`${partner.name} logo`} className={styles.partnerLogo} />
                        : <></>}
                    <span className={getTextClassName()}>{description}</span>
                    {description ?
                        isCollapsed ?
                            <span onClick={toggleIsCollapsed} className={getReadMoreClassName()}><FontAwesomeIcon icon={faChevronDown} /> Read more</span>
                            : <span onClick={toggleIsCollapsed} className={getReadMoreClassName()}><FontAwesomeIcon icon={faChevronUp} /> Read less</span>
                        : <></>
                    }
                </div>
                <div className={styles.hitCounts}>
                    <div className={styles.documentCounts}>
                        <span className={styles.countTitle}>Relevant documents</span>
                        {partner.scienceArticles.length === 0 ? <></> :
                            <DocumentCountBox onClick={() => { onDocumentBoxClick("science"); }} title="Science" icon={faFlask} count={partner.scienceArticles.length} />
                        }
                        {partner.patents.length === 0 ? <></> :
                            <DocumentCountBox onClick={() => { onDocumentBoxClick("patents"); }} title="Patents" icon={faLightbulb} count={partner.patents.length} />
                        }
                        {partner.webpages.length === 0 ? <></> :
                            <DocumentCountBox onClick={() => { onDocumentBoxClick("web"); }} title="Web" icon={faGlobe} count={partner.webpages.length} />
                        }
                    </div>
                </div>
            </div>
            {openedDocumentType ?
                <div>
                    <div style={{ display: "flex", marginTop: "1rem" }}>
                        <SidebarFilterMenu selectedFilterName={openedDocumentType}>
                            {partner.scienceArticles.length === 0 ? <></> :
                                <TitledTypedChild title={`Science (${partner.scienceArticles.length})`} typeName="science">
                                    <DecoupledScienceDocuments 
                                        ids={partner.scienceArticles}
                                        onHighlight={onHighlight ? onScienceHighlight : undefined} 
                                        handleSienceArticleHasBeenUnbookmarked={handleUnderneathDocumentHasBeenUnbookmarked}
                                        handleSienceArticleHasBeenBookmarked={handleUnderneathDocumentHasBeenBookmarked}
                                        amountPerPage={50} />
                                </TitledTypedChild>
                            }
                            {partner.patents.length === 0 ? <></> :
                                <TitledTypedChild title={`Patents (${partner.patents.length})`} typeName="patents">
                                    <DecoupledPatentDocuments 
                                        ids={partner.patents} 
                                        onHighlight={onHighlight ? onPatentHighlight : undefined} 
                                        handlePatentHasBeenUnbookmarked={handleUnderneathDocumentHasBeenUnbookmarked} 
                                        handlePatentHasBeenBookmarked={handleUnderneathDocumentHasBeenBookmarked}
                                        amountPerPage={50} />
                                </TitledTypedChild>
                            }
                            {partner.webpages.length === 0 ? <></> :
                                <TitledTypedChild title={`Web (${partner.webpages.length})`} typeName="web">
                                    <DecoupledWebDocuments ids={partner.webpages} />
                                </TitledTypedChild>
                            }
                        </SidebarFilterMenu>
                    </div>
                </div>
                : <></>
            }
        </div>
    );
};